import type { Course, Lesson, QuizQuestion } from './courses';

// The career courses, as Lagos Life's "How to become a…" short courses (worked out from its public client
// code), written for the UK routes: what the job is, the way in, the training and licences, the first job, and
// the ladder. UK facts as of 2026 (qualification names, regulators, laws); the admins keep them current.
// Each has five lessons with notes and five questions; a certificate eases promotions in the game job it names.

const q = (question: string, right: string, ...wrong: string[]): QuizQuestion => ({ q: question, right, wrong });
const lesson = (title: string, notes: string[], questions: QuizQuestion[]): Lesson => ({ title, notes, questions });
const career = (value: Omit<Course, 'visible'>): Course => ({ ...value, visible: true });

export const CAREER_COURSES: Course[] = [
  career({
    id: 'become_solicitor',
    label: 'How to become a Solicitor',
    emoji: '⚖️',
    blurb:
      'From any degree to the SQE, qualifying work experience and admission by the SRA; and the Bar for barristers.',
    skill: 'charisma',
    career: null,
    feePence: 12000,
    lessons: [
      lesson(
        'What lawyers really do',
        [
          'England and Wales has two kinds of lawyer. Solicitors advise clients, draft contracts and run cases; barristers are specialists in advocacy who argue in court, usually instructed by a solicitor.',
          'Solicitors are regulated by the Solicitors Regulation Authority (SRA); barristers by the Bar Standards Board (BSB). The Law Society and the Bar Council are their professional bodies.',
          'Most of the work is not in a courtroom: reading, writing, negotiating and explaining the law to people who need it in plain words.',
        ],
        [
          q(
            'Who regulates solicitors in England and Wales?',
            'The Solicitors Regulation Authority (SRA)',
            'The Bar Council',
            'The Home Office',
            'The Law Commission',
          ),
          q(
            'Which kind of lawyer specialises in arguing cases in court?',
            'A barrister',
            'A solicitor',
            'A paralegal',
            'A notary',
          ),
          q('Who usually instructs a barrister?', 'A solicitor', 'The judge', 'The police', 'The jury'),
          q('Who regulates barristers?', 'The Bar Standards Board', 'The SRA', 'The Law Society', 'Ofsted'),
          q(
            'Most of a solicitor’s time is spent…',
            'Advising, drafting and negotiating',
            'In the witness box',
            'Arresting suspects',
            'Sentencing',
          ),
        ],
      ),
      lesson(
        'Getting in: a degree, then the SQE',
        [
          'Since 2021 the route to qualify as a solicitor is the Solicitors Qualifying Examination (SQE). You need a degree in any subject (not only law), or an equivalent qualification, before you can be admitted.',
          'SQE1 tests legal knowledge in two long multiple-choice papers; SQE2 tests practical skills like client interviewing, advocacy, drafting and legal research.',
          'Solicitor apprenticeships let school leavers work and study for six years, with the SQE at the end and no student loan.',
        ],
        [
          q(
            'What is the SQE?',
            'The Solicitors Qualifying Examination',
            'A kind of law degree',
            'A court fee',
            'A barristers’ exam',
          ),
          q(
            'Must your degree be in law to take the SQE route?',
            'No, any subject',
            'Yes, always',
            'Only if you want to be a barrister',
            'Only in Scotland',
          ),
          q(
            'What does SQE2 test?',
            'Practical skills like advocacy and drafting',
            'Multiple-choice legal knowledge only',
            'Typing speed',
            'Latin',
          ),
          q(
            'How long is a solicitor apprenticeship, roughly?',
            'About six years',
            'Six months',
            'Two years',
            'Fifteen years',
          ),
          q(
            'SQE1 is made of…',
            'Long multiple-choice papers',
            'An essay on jurisprudence',
            'A mock trial only',
            'A dissertation',
          ),
        ],
      ),
      lesson(
        'Qualifying work experience',
        [
          'Before admission you need two years’ full-time (or equivalent) Qualifying Work Experience (QWE), signed off by a solicitor or a compliance officer. It can be in up to four organisations.',
          'QWE can be a traditional training contract at a firm, but also work as a paralegal, in a law clinic or in-house at a company.',
          'Finally the SRA checks your character and suitability, and you are admitted to the roll of solicitors.',
        ],
        [
          q(
            'How much Qualifying Work Experience is needed?',
            'Two years full-time or equivalent',
            'Six weeks',
            'Ten years',
            'None',
          ),
          q('In how many organisations can QWE be done, at most?', 'Four', 'One', 'Two', 'Ten'),
          q('Who signs off QWE?', 'A solicitor or compliance officer', 'A judge', 'Your university', 'A client'),
          q(
            'Does paralegal work count as QWE?',
            'Yes, if it is signed off',
            'Never',
            'Only at big firms',
            'Only abroad',
          ),
          q(
            'What does the SRA check before admission?',
            'Character and suitability',
            'Your height',
            'Your driving record only',
            'Your school grades only',
          ),
        ],
      ),
      lesson(
        'The Bar: becoming a barrister',
        [
          'Barristers need a law degree or a non-law degree plus a conversion course (the PGDL), then the Bar course (vocational training), then pupillage: a year of training in chambers under a supervisor.',
          'You must join one of the four Inns of Court (Lincoln’s Inn, Inner Temple, Middle Temple, Gray’s Inn), which "calls you to the Bar".',
          'Most barristers are self-employed and share chambers with others, paying rent from their fees; clerks bring in the work.',
        ],
        [
          q('What is pupillage?', 'A year of training in chambers', 'A law degree', 'A kind of court', 'A wig fitting'),
          q('How many Inns of Court are there?', 'Four', 'Two', 'Six', 'Twelve'),
          q(
            'A non-law graduate who wants to be a barrister takes…',
            'The PGDL conversion course',
            'The SQE1 only',
            'A medical degree',
            'Nothing extra',
          ),
          q('Who "calls" a barrister to the Bar?', 'Their Inn of Court', 'The Prime Minister', 'The SRA', 'The police'),
          q(
            'Most barristers are…',
            'Self-employed, sharing chambers',
            'Civil servants',
            'Employed by the courts',
            'Employed by juries',
          ),
        ],
      ),
      lesson(
        'Your career: firms, in-house and KC',
        [
          'Newly qualified solicitors join firms as associates. "Magic circle" City firms pay the most and work the longest hours; high street firms do conveyancing, wills, family and crime.',
          'In-house lawyers work for one company; others work for the Government Legal Department or the Crown Prosecution Service (CPS).',
          'Partners share in a firm’s profits. A barrister of great standing can be appointed King’s Counsel (KC), known as "taking silk".',
        ],
        [
          q('What is a KC?', 'King’s Counsel: a senior barrister', 'A kind of judge', 'A law student', 'A court clerk'),
          q(
            '"Taking silk" means…',
            'Being appointed King’s Counsel',
            'Buying a gown',
            'Leaving the law',
            'Joining a jury',
          ),
          q(
            'Which lawyers prosecute criminal cases in England and Wales?',
            'The Crown Prosecution Service',
            'High street conveyancers',
            'The Law Society',
            'The Inns of Court',
          ),
          q(
            'Partners in a firm…',
            'Share its profits',
            'Are paid by the hour only',
            'Cannot practise',
            'Are always barristers',
          ),
          q('A solicitor who works for one company is called…', 'In-house', 'A pupil', 'A silk', 'A clerk'),
        ],
      ),
    ],
  }),

  career({
    id: 'become_doctor',
    label: 'How to become a Doctor',
    emoji: '🧑🏾‍⚕️',
    blurb: 'Medical school, the Foundation Programme, GMC registration, specialty training and the consultant’s round.',
    skill: 'fitness',
    career: null,
    feePence: 15000,
    lessons: [
      lesson(
        'What doctors do',
        [
          'Doctors in the UK work mostly in the NHS: as GPs in the community, or in hospitals as surgeons, physicians, anaesthetists, psychiatrists and dozens of other specialties.',
          'Every doctor must be registered with the General Medical Council (GMC) and hold a licence to practise. The GMC can suspend or strike off doctors who fall short.',
          'The work is long and the hours unsocial, but it is the job people trust most.',
        ],
        [
          q(
            'Who registers doctors in the UK?',
            'The General Medical Council (GMC)',
            'The NHS Trust',
            'The BMA',
            'The Department of Health alone',
          ),
          q('A GP works mainly…', 'In the community', 'In operating theatres', 'In laboratories', 'At sea'),
          q('A doctor who gives anaesthetics is…', 'An anaesthetist', 'A radiologist', 'A pharmacist', 'A paramedic'),
          q('Most UK doctors work for…', 'The NHS', 'Private hospitals only', 'The Army', 'Insurance companies'),
          q(
            'What can the GMC do to a doctor who falls short?',
            'Suspend or strike them off',
            'Fine their patients',
            'Close the hospital',
            'Nothing',
          ),
        ],
      ),
      lesson(
        'Getting in: medical school',
        [
          'A standard medical degree (MBBS or MBChB) takes five years; graduate-entry courses take four. Most schools want top grades in chemistry and biology and a strong UCAT admissions test score.',
          'Applications go through UCAS with an earlier deadline (mid-October). Interviews are often "multiple mini interviews" with short stations.',
          'Medical degree apprenticeships are a newer route, working for the NHS while you study.',
        ],
        [
          q('How long is a standard UK medical degree?', 'Five years', 'Three years', 'Two years', 'Ten years'),
          q(
            'What is the UCAT?',
            'An admissions test for medicine',
            'A medical licence',
            'A surgical tool',
            'A type of degree',
          ),
          q(
            'Medical school UCAS applications close…',
            'In mid-October, earlier than most',
            'In July',
            'In January, like most',
            'Any time',
          ),
          q('Graduate-entry medicine usually takes…', 'Four years', 'One year', 'Seven years', 'Six months'),
          q('MBBS and MBChB are…', 'Medical degrees', 'Nursing degrees', 'Hospital ranks', 'Drug names'),
        ],
      ),
      lesson(
        'Inside medical school',
        [
          'The first years are mostly lectures, anatomy and the science of the body; the later years are clinical placements on wards and in GP surgeries, learning from patients.',
          'Students learn to take a history, examine, and present a case to a senior. Before graduating, every student sits the national Medical Licensing Assessment.',
          'A year "intercalating" lets students add an extra degree, often a BSc in a science.',
        ],
        [
          q(
            'Later years of medical school are mostly…',
            'Clinical placements with patients',
            'Lectures only',
            'Exams only',
            'Lab work only',
          ),
          q(
            'What is the Medical Licensing Assessment?',
            'A national test before graduating',
            'A GP contract',
            'A hospital bed',
            'A drug trial',
          ),
          q(
            '"Intercalating" means…',
            'Taking an extra degree in a year out',
            'Failing a year',
            'Working abroad',
            'Sitting a resit',
          ),
          q(
            '"Taking a history" means…',
            'Asking the patient about their illness',
            'Reading a textbook',
            'Writing an essay',
            'Checking the archives',
          ),
          q(
            'Students learn to present a case to…',
            'A senior doctor',
            'The patient’s family only',
            'The newspaper',
            'The GMC',
          ),
        ],
      ),
      lesson(
        'The Foundation Programme and your licence',
        [
          'After graduating, new doctors do the two-year Foundation Programme: FY1 and FY2, rotating through specialties every few months.',
          'Provisional GMC registration covers FY1; full registration with a licence to practise comes after it. Doctors must then revalidate every five years.',
          'Foundation doctors are paid, work shifts including nights, and are supervised by registrars and consultants.',
        ],
        [
          q('How long is the Foundation Programme?', 'Two years', 'Six months', 'Five years', 'One week'),
          q('Full GMC registration comes after…', 'FY1', 'Medical school only', 'Consultant level', 'Retirement'),
          q('How often must doctors revalidate?', 'Every five years', 'Every year', 'Never', 'Every twenty years'),
          q(
            'Foundation doctors rotate through…',
            'Different specialties',
            'Different countries',
            'Different hospitals abroad',
            'Only one ward',
          ),
          q('FY1 and FY2 stand for…', 'Foundation Year 1 and 2', 'First Year 1 and 2', 'Final Year', 'Fellowship Year'),
        ],
      ),
      lesson(
        'Specialising and the consultant’s round',
        [
          'After foundation, doctors apply for specialty training. GP training takes three years; hospital specialties take around five to eight, with Royal College exams (MRCP, MRCS and others) along the way.',
          'Trainees are "registrars". Finishing training earns a Certificate of Completion of Training and a place on the specialist or GP register.',
          'Consultants lead teams and hold the final say on treatment; many also teach, research or take private patients.',
        ],
        [
          q('How long is GP specialty training?', 'Three years', 'One year', 'Eight years', 'Three months'),
          q('MRCP and MRCS are…', 'Royal College exams', 'Hospital wards', 'Pay grades', 'Types of medicine'),
          q('A doctor in specialty training is called…', 'A registrar', 'A consultant', 'A porter', 'A matron'),
          q(
            'What does finishing specialty training earn?',
            'A Certificate of Completion of Training',
            'A knighthood',
            'A new degree',
            'A GMC fine',
          ),
          q(
            'Who has the final say on a hospital patient’s treatment?',
            'The consultant',
            'The foundation doctor',
            'The receptionist',
            'The hospital shop',
          ),
        ],
      ),
    ],
  }),

  career({
    id: 'become_developer',
    label: 'How to become a Software Developer',
    emoji: '💻',
    blurb: 'What developers do, which path to take, how to learn, a portfolio people can see, and landing the job.',
    skill: 'coding',
    career: 'office_shift',
    feePence: 10000,
    lessons: [
      lesson(
        'What developers actually do',
        [
          'Developers build and fix software: websites, apps, the systems behind banks and shops. The day is reading code, writing a little, testing it and talking to the team about what to build.',
          'Front-end developers work on what users see; back-end developers on servers and databases; full-stack on both. Data, mobile and games are paths of their own.',
          'Most teams work in "agile" sprints of a week or two, with a short daily stand-up meeting.',
        ],
        [
          q(
            'A front-end developer works on…',
            'What users see',
            'Servers and databases',
            'Office furniture',
            'Payroll',
          ),
          q('What is a stand-up?', 'A short daily team meeting', 'A comedy night', 'A server rack', 'A kind of test'),
          q('A sprint usually lasts…', 'A week or two', 'An hour', 'A year', 'Ten seconds'),
          q(
            'A full-stack developer works on…',
            'Both front-end and back-end',
            'Only design',
            'Only hardware',
            'Only sales',
          ),
          q(
            'Most of a developer’s day is…',
            'Reading, writing and testing code with the team',
            'Typing as fast as possible',
            'Buying computers',
            'Fixing printers',
          ),
        ],
      ),
      lesson(
        'Pick a path and a language',
        [
          'JavaScript and TypeScript run the web; Python is loved for data, automation and beginners; Java and C# run big company systems; Swift and Kotlin build iPhone and Android apps.',
          'Start with one language and learn it well. The ideas (variables, loops, functions, data) carry over to the next.',
          'Git tracks every change to code and lets teams work on it together; GitHub hosts it.',
        ],
        [
          q('Which language runs in every web browser?', 'JavaScript', 'Python', 'C#', 'Swift'),
          q('Which is a good first language for data and automation?', 'Python', 'Assembly', 'COBOL', 'Fortran'),
          q('Kotlin is used mainly for…', 'Android apps', 'Spreadsheets', 'Printers', 'Mainframes'),
          q('What does Git do?', 'Tracks changes to code', 'Draws icons', 'Hosts websites only', 'Sends email'),
          q(
            'How many languages should a beginner start with?',
            'One, learned well',
            'Five at once',
            'None until a degree',
            'All of them',
          ),
        ],
      ),
      lesson(
        'How to learn: degree, apprenticeship, bootcamp or self-taught',
        [
          'A computer science degree takes three years (four with a placement year). A degree apprenticeship (Level 6) pays you to work while you study, with no tuition fees.',
          'Bootcamps teach web development in 12 to 16 intensive weeks. Self-taught developers use free courses and build projects; plenty get hired.',
          'Whichever route, employers hire on what you can build and explain, not the certificate alone.',
        ],
        [
          q(
            'A degree apprenticeship…',
            'Pays you to work while you study',
            'Costs full tuition fees',
            'Takes one week',
            'Is only for graduates',
          ),
          q('How long is a typical coding bootcamp?', '12 to 16 weeks', 'Three years', 'One day', 'Ten years'),
          q(
            'What do employers hire on most?',
            'What you can build and explain',
            'Your handwriting',
            'Your age',
            'The colour of your laptop',
          ),
          q(
            'A placement year in a degree is…',
            'A year working in industry',
            'A year off',
            'A resit year',
            'A year abroad only',
          ),
          q(
            'Can self-taught developers get hired?',
            'Yes, with projects to show',
            'Never',
            'Only in America',
            'Only with a PhD',
          ),
        ],
      ),
      lesson(
        'Build a portfolio people can see',
        [
          'Put your projects on GitHub with a README that says what they do and how to run them. A live link is better than a screenshot.',
          'Build things you would use: a budget tracker, a bus-time app for your stop, a tool for a friend’s shop. Real problems make better stories in interviews.',
          'Contributing to open-source projects, even fixing typos in docs, shows you can work with other people’s code.',
        ],
        [
          q(
            'What should every project have on GitHub?',
            'A README saying what it does and how to run it',
            'A password',
            'A price',
            'A logo only',
          ),
          q(
            'Why build things you would use?',
            'They make better interview stories',
            'They are easier',
            'They need no code',
            'Employers ban other projects',
          ),
          q('A live link to a project is better than…', 'A screenshot', 'A README', 'A test', 'A domain name'),
          q(
            'Contributing to open source shows…',
            'You can work with other people’s code',
            'You own the project',
            'You never make mistakes',
            'You are a manager',
          ),
          q(
            'What is open source?',
            'Code anyone can read and contribute to',
            'Code that costs money',
            'Code with no licence',
            'Code only on paper',
          ),
        ],
      ),
      lesson(
        'Landing the job',
        [
          'Junior roles are the first step; apply widely, including to companies outside tech, which also need developers. London pays more but costs more.',
          'Interviews usually include a coding exercise or a take-home task, then a chat about how you think and work with others.',
          'Keep learning on the job: code reviews, pairing with seniors and reading other people’s pull requests teach more than any course.',
        ],
        [
          q(
            'A first developer job is usually called…',
            'Junior developer',
            'Chief technology officer',
            'Principal engineer',
            'Architect',
          ),
          q(
            'What is a take-home task?',
            'A coding exercise done before the interview',
            'Homework from school',
            'A free laptop',
            'A pay slip',
          ),
          q(
            'What is a pull request?',
            'A proposed change to code for review',
            'A job application',
            'A kind of loop',
            'A server',
          ),
          q(
            'Do only tech companies hire developers?',
            'No, most big companies do',
            'Yes',
            'Only banks',
            'Only games studios',
          ),
          q(
            'A code review is…',
            'Another developer checking your change',
            'A film review',
            'A performance appraisal',
            'A test suite',
          ),
        ],
      ),
    ],
  }),

  career({
    id: 'become_banker',
    label: 'How to become a Banker',
    emoji: '🏦',
    blurb: 'Retail, corporate and investment banking, the regulators, KYC, targets and the Chartered Banker route.',
    skill: 'charisma',
    career: null,
    feePence: 9000,
    lessons: [
      lesson(
        'What bankers do',
        [
          'Retail banks serve the public: current accounts, mortgages and savings. Corporate banks lend to companies. Investment banks raise money for companies and governments and trade markets.',
          'The Bank of England is the UK’s central bank: it sets Bank Rate through its Monetary Policy Committee and prints the notes.',
          'Branch staff, relationship managers, analysts, traders and compliance officers are all "bankers", with very different days.',
        ],
        [
          q('Which bank sets Bank Rate?', 'The Bank of England', 'Barclays', 'The Treasury', 'HSBC'),
          q('Retail banking serves…', 'The public', 'Governments only', 'Shops only', 'Other banks only'),
          q(
            'Investment banks…',
            'Raise money for companies and trade markets',
            'Run cash machines only',
            'Set taxes',
            'Print banknotes',
          ),
          q(
            'Who decides Bank Rate inside the Bank of England?',
            'The Monetary Policy Committee',
            'The cashiers',
            'The Prime Minister',
            'The FCA',
          ),
          q(
            'A corporate bank mainly…',
            'Lends to companies',
            'Sells insurance door to door',
            'Runs the stock exchange',
            'Prints money',
          ),
        ],
      ),
      lesson(
        'The way in: graduate schemes and apprenticeships',
        [
          'Big banks run graduate schemes lasting around two years, rotating through teams. Applications open in autumn, a year before you start, with online tests and assessment centres.',
          'Apprenticeships from Level 3 to degree level take school leavers straight into branches and head offices.',
          'Any degree can work for retail and corporate banking; investment banks favour numerate subjects and strong grades.',
        ],
        [
          q('How long is a typical bank graduate scheme?', 'Around two years', 'Six months', 'Ten years', 'One week'),
          q(
            'When do graduate applications usually open?',
            'In autumn, a year before you start',
            'The day you start',
            'In June',
            'Never',
          ),
          q(
            'What is an assessment centre?',
            'A day of group tasks and interviews',
            'A branch',
            'A cash machine',
            'An exam hall for A-levels',
          ),
          q(
            'Can a school leaver join a bank?',
            'Yes, through an apprenticeship',
            'No',
            'Only as a customer',
            'Only abroad',
          ),
          q(
            'Investment banks favour…',
            'Numerate degrees and strong grades',
            'No qualifications',
            'Art degrees only',
            'Sports results',
          ),
        ],
      ),
      lesson(
        'The rules: the FCA, the PRA and KYC',
        [
          'Two regulators watch UK banks: the Financial Conduct Authority (FCA) for how they treat customers, and the Prudential Regulation Authority (PRA), part of the Bank of England, for their safety.',
          'Know Your Customer (KYC) means checking who a customer is before opening an account; anti-money-laundering (AML) rules make banks report suspicious activity.',
          'The Financial Services Compensation Scheme (FSCS) protects customers’ deposits up to a limit if a bank fails.',
        ],
        [
          q(
            'What does KYC stand for?',
            'Know Your Customer',
            'Keep Your Cash',
            'Key Yearly Charge',
            'Kindly Yield Credit',
          ),
          q('Which regulator looks at how banks treat customers?', 'The FCA', 'The PRA', 'The ONS', 'The BBC'),
          q('The PRA is part of…', 'The Bank of England', 'The Treasury', 'The Post Office', 'HMRC'),
          q(
            'What does the FSCS do?',
            'Protects deposits if a bank fails',
            'Sets mortgage rates',
            'Prints cheques',
            'Issues passports',
          ),
          q(
            'AML rules require banks to…',
            'Report suspicious activity',
            'Pay no interest',
            'Close on Fridays',
            'Refuse students',
          ),
        ],
      ),
      lesson(
        'Targets, customers and the Chartered Banker',
        [
          'Branch and relationship roles have targets for accounts opened and loans made, but since the payment protection insurance scandal the rules put customers’ interests first (the FCA’s Consumer Duty).',
          'The Chartered Banker Institute and the London Institute of Banking & Finance offer professional qualifications; the CFA is the gold standard in investment.',
          'Good bankers explain products honestly and say no to a loan a customer cannot afford.',
        ],
        [
          q(
            'The FCA’s Consumer Duty puts…',
            'Customers’ interests first',
            'Targets first',
            'Shareholders first',
            'Bonuses first',
          ),
          q('PPI was…', 'A mis-selling scandal', 'A kind of mortgage', 'A bank holiday', 'A regulator'),
          q('Which qualification is the gold standard in investment?', 'The CFA', 'The MOT', 'The GCSE', 'The SQE'),
          q(
            'The Chartered Banker Institute offers…',
            'Professional banking qualifications',
            'Mortgages',
            'Bank accounts',
            'Savings bonds',
          ),
          q(
            'A customer cannot afford a loan. A good banker…',
            'Says no and explains',
            'Lends anyway for the target',
            'Doubles the loan',
            'Hides the rate',
          ),
        ],
      ),
      lesson(
        'Climbing the ladder',
        [
          'Analyst, associate, vice president, director, managing director: the investment bank ladder. In retail, branch manager, area manager, then head office.',
          'Bonuses are large in investment banking and reviewed yearly; the hours in your first years can be very long.',
          'Many bankers move on to fintech, private equity, or to run companies they once advised.',
        ],
        [
          q(
            'The first rung on the investment bank ladder is…',
            'Analyst',
            'Managing director',
            'Vice president',
            'Chairman',
          ),
          q('In retail banking, after branch manager often comes…', 'Area manager', 'Analyst', 'Trader', 'Governor'),
          q('How often are bonuses usually reviewed?', 'Yearly', 'Hourly', 'Every ten years', 'Never'),
          q('"Fintech" means…', 'Financial technology companies', 'A bank branch', 'A tax', 'A savings account'),
          q('MD in a bank stands for…', 'Managing director', 'Medical doctor', 'Money dealer', 'Mortgage desk'),
        ],
      ),
    ],
  }),

  career({
    id: 'become_chef',
    label: 'How to become a Chef',
    emoji: '🍳',
    blurb:
      'The kitchen brigade, food safety, knife skills, training on the job or at college, and your own food business.',
    skill: 'cooking',
    career: 'pub_shift',
    feePence: 6000,
    lessons: [
      lesson(
        'Life in a professional kitchen',
        [
          'Kitchens run on the brigade system: the head chef leads, the sous chef is second, chefs de partie run sections (sauce, grill, pastry), and commis chefs are the juniors learning.',
          '"Mise en place" is everything prepared before service begins; service is the rush when orders come in. "Yes, chef" keeps it moving.',
          'Shifts are long, hot and on your feet, with evenings and weekends busiest.',
        ],
        [
          q(
            'Who is second in command in a kitchen?',
            'The sous chef',
            'The commis chef',
            'The waiter',
            'The pastry chef',
          ),
          q('A chef de partie…', 'Runs a section of the kitchen', 'Washes up', 'Owns the restaurant', 'Greets guests'),
          q(
            'What does "mise en place" mean?',
            'Everything prepared before service',
            'The bill',
            'The dining room',
            'A sauce',
          ),
          q(
            'The junior chef learning the trade is…',
            'The commis chef',
            'The head chef',
            'The sommelier',
            'The maître d’',
          ),
          q('When are kitchens busiest?', 'Evenings and weekends', 'Monday mornings', 'Mid-afternoon', '3 am'),
        ],
      ),
      lesson(
        'Food safety comes first',
        [
          'Every chef needs a Level 2 Food Hygiene certificate. The 4 Cs are cleaning, cooking, chilling and cross-contamination.',
          'Fridges at 5°C or below, cooked food served at 63°C or above, raw meat on the bottom shelf, and the 14 allergens known by heart.',
          'Councils inspect and rate kitchens from 0 to 5; a bad rating empties a restaurant faster than a bad review.',
        ],
        [
          q('Which food hygiene level does a chef need?', 'Level 2', 'Level 7', 'None', 'Level 0'),
          q('Hot food is held for serving at…', '63°C or above', '30°C', '45°C', 'Room temperature'),
          q('How many major allergens must kitchens know?', '14', '3', '7', '40'),
          q(
            'Raw meat goes…',
            'On the bottom shelf of the fridge',
            'On the top shelf',
            'Next to salad',
            'In the freezer door',
          ),
          q('The best food hygiene rating is…', '5', '1', '10', 'A'),
        ],
      ),
      lesson(
        'Knife skills and the basics',
        [
          'A chef’s knife, a paring knife and a serrated bread knife do most of the work. Keep them sharp: a blunt knife slips.',
          'The classic cuts: julienne (matchsticks), brunoise (tiny dice), chiffonade (ribbons of leaves). The claw grip keeps fingertips tucked.',
          'The five French mother sauces (béchamel, velouté, espagnole, hollandaise, tomato) underpin hundreds of dishes.',
        ],
        [
          q(
            'Why keep knives sharp?',
            'A blunt knife slips',
            'Sharp knives are cheaper',
            'It looks good',
            'The law says so',
          ),
          q('Julienne is…', 'Matchstick cuts', 'Tiny dice', 'Ribbons of leaves', 'A sauce'),
          q('How many French mother sauces are there?', 'Five', 'Two', 'Twelve', 'Fifty'),
          q(
            'The claw grip…',
            'Keeps fingertips tucked away from the blade',
            'Is for carving meat only',
            'Is a kind of knife',
            'Is for the oven',
          ),
          q('Brunoise is…', 'Tiny dice', 'A bread knife', 'A whisk', 'A roast'),
        ],
      ),
      lesson(
        'Training: college, apprenticeships and hotels',
        [
          'The Commis Chef Level 2 and Chef de Partie Level 3 apprenticeships pay you to learn in a real kitchen; colleges offer Professional Cookery diplomas.',
          'Hotel kitchens train chefs across every section; many top chefs started in a hotel or a Michelin-starred restaurant on low pay to learn.',
          'A "stage" (unpaid trial) in a great kitchen is a classic way to learn and get noticed, though pay rules now limit how long they can be.',
        ],
        [
          q('A Commis Chef apprenticeship is…', 'Level 2', 'Level 6', 'A degree', 'Unpaid'),
          q('What is a "stage" in a kitchen?', 'A short trial or placement', 'A platform', 'A sauce', 'A kind of oven'),
          q(
            'Why train in a hotel kitchen?',
            'It covers every section',
            'It pays the most',
            'It has no service',
            'It needs no hygiene certificate',
          ),
          q(
            'A college route to cooking is…',
            'A Professional Cookery diploma',
            'A law degree',
            'An MBA',
            'A driving licence',
          ),
          q('Michelin stars are awarded to…', 'Restaurants', 'Chefs’ knives', 'Hotels’ pools', 'Food trucks only'),
        ],
      ),
      lesson(
        'Your own food business',
        [
          'Register your food business with the council at least 28 days before trading: free, and the law. Then the inspection and your hygiene rating.',
          'Street food and markets are the cheapest way to start: a stall, a gazebo and a product people queue for. Work out food cost (usually 25 to 35% of the menu price).',
          'Allergen information, Natasha’s Law labels on prepacked food, and a good accountant keep you out of trouble.',
        ],
        [
          q(
            'How far ahead must a food business register with the council?',
            'At least 28 days',
            'The day it opens',
            'A year',
            'It need not register',
          ),
          q(
            'A typical food cost is…',
            '25 to 35% of the menu price',
            '90% of the price',
            '1% of the price',
            'Equal to the price',
          ),
          q(
            'What is the cheapest way to start a food business?',
            'A market stall or street food',
            'A hotel',
            'A chain of restaurants',
            'A factory',
          ),
          q('Natasha’s Law covers…', 'Labels on food prepacked for direct sale', 'Wages', 'Parking', 'Table bookings'),
          q('Registering a food business costs…', 'Nothing', '£1,000', '£50 a month', 'A share of profits'),
        ],
      ),
    ],
  }),

  career({
    id: 'become_musician',
    label: 'How to become a Musician',
    emoji: '🎤',
    blurb:
      'The music business, your craft and your sound, recording, getting heard, and the money: royalties and contracts.',
    skill: 'music',
    career: 'busk',
    feePence: 7000,
    lessons: [
      lesson(
        'The music business: who does what',
        [
          'Artists write and perform. Managers run their careers for a cut (often 15 to 20%). Labels fund and release records; publishers look after the songs themselves.',
          'Promoters put on gigs; booking agents get artists onto them. The Musicians’ Union advises members on contracts and pay.',
          'Streaming pays per play; live shows and merchandise are where most working musicians make their living.',
        ],
        [
          q('What does a manager usually take?', 'A cut, often 15 to 20%', 'Nothing', 'Half', 'All the royalties'),
          q(
            'A music publisher looks after…',
            'The songs (the compositions)',
            'The gigs',
            'The tour bus',
            'The merchandise',
          ),
          q('Who puts on gigs?', 'Promoters', 'Publishers', 'Labels only', 'Pluggers'),
          q('Which union advises musicians on contracts?', 'The Musicians’ Union', 'Unite', 'The RMT', 'Equity only'),
          q(
            'Where do most working musicians earn most?',
            'Live shows and merchandise',
            'Streaming alone',
            'Radio play',
            'Sheet music',
          ),
        ],
      ),
      lesson(
        'Your craft and your sound',
        [
          'Practise daily, in short focused sessions: scales, songs, and writing. Record yourself and listen back; it is the fastest way to improve.',
          'Grades (ABRSM, Trinity, Rockschool) and music college (the Royal Academy, Guildhall, BIMM, ACM) give structure, but nobody asks a gigging musician for a certificate.',
          'Find your sound by copying the artists you love until something of your own comes out.',
        ],
        [
          q(
            'What is the fastest way to improve?',
            'Record yourself and listen back',
            'Buy a better guitar',
            'Practise once a month',
            'Only play live',
          ),
          q('Rockschool and ABRSM are…', 'Graded music exam boards', 'Record labels', 'Venues', 'Streaming apps'),
          q(
            'Do gigging musicians need a certificate?',
            'No, nobody asks',
            'Yes, by law',
            'Only drummers',
            'Only abroad',
          ),
          q(
            'Good practice is…',
            'Daily, short and focused',
            'Once a week for eight hours',
            'Only before a gig',
            'Only with a teacher',
          ),
          q(
            'How do most artists find their sound?',
            'Copying what they love until their own comes out',
            'Buying it',
            'Waiting',
            'Asking a label',
          ),
        ],
      ),
      lesson(
        'Recording your music',
        [
          'A laptop, an audio interface, one good microphone and a DAW (Logic, Ableton, FL Studio, or free ones like GarageBand) are a home studio.',
          'Record the rhythm first, then layers, then vocals. Mixing balances the parts; mastering makes the finished track loud and even across speakers.',
          'A studio and a producer cost money but bring experience; many artists do early releases at home and save the studio for the big one.',
        ],
        [
          q('What is a DAW?', 'Software for recording and editing music', 'A microphone', 'A record label', 'A venue'),
          q(
            'Mastering…',
            'Makes the finished track loud and even',
            'Writes the lyrics',
            'Books the gig',
            'Designs the cover',
          ),
          q('What do you record first, usually?', 'The rhythm', 'The vocals', 'The artwork', 'The solo'),
          q(
            'An audio interface…',
            'Connects microphones and instruments to the computer',
            'Is a kind of speaker',
            'Is a streaming service',
            'Is a contract',
          ),
          q('Mixing is…', 'Balancing the parts of a track', 'Cutting a vinyl', 'Signing a label', 'Tuning a guitar'),
        ],
      ),
      lesson(
        'Getting heard',
        [
          'Distributors put your track on Spotify, Apple Music and the rest for a small fee; pitch it to playlists weeks before release.',
          'Play live: open mics, support slots, then your own shows. In London, licensed busking pitches on the Underground are auditioned by Transport for London.',
          'Short videos online and a mailing list you own matter more than any single platform’s algorithm.',
        ],
        [
          q(
            'How does an independent artist get on Spotify?',
            'Through a distributor',
            'By emailing Spotify',
            'Only with a label',
            'By busking',
          ),
          q(
            'Who runs licensed busking pitches on the London Underground?',
            'Transport for London',
            'The Musicians’ Union',
            'The Met Police',
            'Westminster Council',
          ),
          q(
            'When should you pitch a track to playlists?',
            'Weeks before release',
            'A year after',
            'Never',
            'Only after it charts',
          ),
          q('A support slot is…', 'Playing before a bigger act', 'A sound check', 'A radio interview', 'A rehearsal'),
          q(
            'Why keep a mailing list?',
            'You own it; no algorithm controls it',
            'It is the law',
            'Labels demand it',
            'It replaces gigs',
          ),
        ],
      ),
      lesson(
        'Money, rights and contracts',
        [
          'A song has two copyrights: the composition (writers) and the recording (the "master"). Register with PRS for Music to collect royalties when your songs are played or performed, and with PPL for the recordings.',
          'Record deals advance money against future royalties; the advance is paid back from your share before you see a penny more. A 360 deal takes a cut of everything, gigs included.',
          'Never sign without a music lawyer reading it. Splits between co-writers should be agreed in writing on the day.',
        ],
        [
          q(
            'PRS for Music collects…',
            'Royalties for songwriters and publishers',
            'Ticket money',
            'Tax',
            'Instrument insurance',
          ),
          q('The "master" is…', 'The recording’s copyright', 'The best musician', 'The producer', 'The venue'),
          q('An advance is…', 'Money paid back from your future royalties', 'A gift', 'A fine', 'A tax rebate'),
          q('A 360 deal takes a cut of…', 'Everything, gigs included', 'Streaming only', 'Nothing', 'Merchandise only'),
          q(
            'Before signing a contract you should…',
            'Have a music lawyer read it',
            'Sign fast before they change their mind',
            'Ask a fan',
            'Post it online',
          ),
        ],
      ),
    ],
  }),

  career({
    id: 'become_fashion_designer',
    label: 'How to become a Fashion Designer',
    emoji: '👗',
    blurb: 'Learning the craft, knowing your fabrics, pricing your work, running the business and building a brand.',
    skill: 'hustle',
    career: 'run_stall',
    feePence: 7000,
    lessons: [
      lesson(
        'What fashion designers do',
        [
          'Designers research trends, sketch, choose fabrics, make patterns and samples, and work with factories or make pieces themselves.',
          'Jobs range from high street brands (designing ranges a year ahead) to luxury houses, costume for film and theatre, and your own label.',
          'London Fashion Week, run by the British Fashion Council, shows twice a year and launches careers.',
        ],
        [
          q('Who runs London Fashion Week?', 'The British Fashion Council', 'The BBC', 'Westminster Council', 'Vogue'),
          q(
            'High street brands design ranges…',
            'About a year ahead',
            'The day before',
            'Ten years ahead',
            'After they sell',
          ),
          q('A sample is…', 'A first made-up version of a design', 'A fabric shop', 'A fashion show', 'A customer'),
          q('How often does London Fashion Week show?', 'Twice a year', 'Weekly', 'Once a decade', 'Daily'),
          q('Which is a fashion design job?', 'Costume for film and theatre', 'Dentistry', 'Plumbing', 'Accountancy'),
        ],
      ),
      lesson(
        'Learning the craft',
        [
          'Fashion degrees at Central Saint Martins, the London College of Fashion and others teach drawing, pattern cutting and construction; a sewing machine and YouTube teach plenty too.',
          'A portfolio of your work (sketches, finished pieces, photos on people) is what gets you in, more than the grade.',
          'Internships with designers are the usual first step; the law says interns doing real work must be paid at least the minimum wage.',
        ],
        [
          q(
            'What gets a designer into a job most?',
            'A portfolio of their work',
            'A driving licence',
            'Their height',
            'A maths A-level',
          ),
          q(
            'Pattern cutting is…',
            'Making the paper shapes a garment is cut from',
            'Cutting prices',
            'Cutting hair',
            'A kind of fabric',
          ),
          q(
            'Interns doing real work must be paid…',
            'At least the minimum wage',
            'Nothing',
            'In clothes',
            'Only expenses',
          ),
          q(
            'Central Saint Martins is…',
            'A fashion and art college in London',
            'A department store',
            'A fabric',
            'A magazine',
          ),
          q(
            'Can you learn construction without a degree?',
            'Yes, a sewing machine and practice',
            'No',
            'Only in Paris',
            'Only with a tailor',
          ),
        ],
      ),
      lesson(
        'Know your fabrics',
        [
          'Natural fibres: cotton, linen, wool and silk. Synthetics: polyester, nylon, acrylic. Blends mix them for cost, stretch or easy care.',
          'Fabric is bought by the metre; "grain" is the direction of the threads, and cutting on the bias (diagonal) gives drape.',
          'UK law requires garments to be labelled with their fibre content; care symbols tell the customer how to wash them.',
        ],
        [
          q('Which of these is a natural fibre?', 'Linen', 'Polyester', 'Nylon', 'Acrylic'),
          q(
            'Cutting on the bias means…',
            'Cutting diagonally for drape',
            'Cutting cheaply',
            'Cutting in the dark',
            'Cutting with scissors only',
          ),
          q(
            'UK law requires garments to show…',
            'Their fibre content',
            'The designer’s age',
            'The price in euros',
            'A photo',
          ),
          q('Fabric is usually sold by…', 'The metre', 'The kilogram', 'The hour', 'The piece only'),
          q('A blend is…', 'A mix of fibres', 'A colour', 'A stitch', 'A shop'),
        ],
      ),
      lesson(
        'Running the business',
        [
          'Price = materials + labour + overheads, then your margin. Many designers wholesale at about double cost and retail at about double wholesale.',
          'Register as a sole trader with HMRC or a limited company at Companies House. Keep every receipt. Small batches avoid unsold stock.',
          'Sell at markets and pop-ups first (a stall is cheap), then online and to boutiques who buy wholesale.',
        ],
        [
          q(
            'A common retail price is about…',
            'Double the wholesale price',
            'The cost of materials',
            'Ten times cost',
            'Whatever the customer offers',
          ),
          q(
            'Why make small batches?',
            'To avoid unsold stock',
            'They are cheaper per piece',
            'The law requires it',
            'Factories refuse big ones',
          ),
          q('A limited company is registered at…', 'Companies House', 'HMRC only', 'The council', 'The bank'),
          q(
            'What should you keep for the accounts?',
            'Every receipt',
            'Nothing',
            'Only big invoices',
            'Only cash sales',
          ),
          q(
            'A cheap way to start selling is…',
            'A market stall or pop-up',
            'A Bond Street shop',
            'A TV advert',
            'A factory',
          ),
        ],
      ),
      lesson(
        'Building a brand',
        [
          'A brand is a point of view: who it is for, what it stands for, and a look that is the same on the label, the website and the posts.',
          'Register your name as a trade mark with the UK Intellectual Property Office so nobody else can use it.',
          'Collaborations, dressing people who get photographed, and a consistent story online build a following that buys.',
        ],
        [
          q(
            'Where do you register a trade mark in the UK?',
            'The Intellectual Property Office',
            'Companies House',
            'The council',
            'The British Fashion Council',
          ),
          q('A brand is…', 'A point of view and a consistent look', 'A logo only', 'A price', 'A shop'),
          q(
            'A collaboration is…',
            'Working with another brand or artist on a range',
            'A sale',
            'A lawsuit',
            'A fabric',
          ),
          q(
            'What should look the same on the label, website and posts?',
            'The brand’s look',
            'The price',
            'The size',
            'The fabric',
          ),
          q(
            'A following that buys comes from…',
            'A consistent story over time',
            'One viral post',
            'Luck only',
            'A bigger logo',
          ),
        ],
      ),
    ],
  }),

  career({
    id: 'become_pilot',
    label: 'How to become a Pilot',
    emoji: '✈️',
    blurb: 'Medicals, flying school, the PPL and CPL, hours, type ratings and the long road to the captain’s seat.',
    skill: 'fitness',
    career: 'air_checkin',
    feePence: 15000,
    lessons: [
      lesson(
        'What pilots do',
        [
          'Airline pilots fly passengers and freight to a schedule; the captain commands, the first officer shares the flying. Both are fully qualified.',
          'Before every flight: weather, fuel, the flight plan and the aircraft’s technical log. In the air: checklists, air traffic control and constant monitoring.',
          'The UK Civil Aviation Authority (CAA) licenses pilots and oversees safety.',
        ],
        [
          q(
            'Who licenses UK pilots?',
            'The Civil Aviation Authority',
            'The DVLA',
            'The Home Office',
            'Heathrow Airport',
          ),
          q('Who commands an airliner?', 'The captain', 'The purser', 'The first officer', 'Air traffic control'),
          q('Is the first officer fully qualified?', 'Yes', 'No, a trainee', 'Only on short flights', 'Only at night'),
          q(
            'Before a flight pilots check…',
            'Weather, fuel and the flight plan',
            'The in-flight film',
            'Ticket prices',
            'The duty-free',
          ),
          q('In flight, pilots talk to…', 'Air traffic control', 'The airline’s CEO', 'Passengers only', 'The CAA'),
        ],
      ),
      lesson(
        'Before you fly: subjects, medicals and school',
        [
          'Maths, physics and English help; good eyesight (glasses are fine within limits) and health matter more. A Class 1 medical certificate from a CAA-approved doctor is required before commercial training.',
          'Training is "integrated" (one full-time course, around 18 months, often £100,000 or more) or "modular" (step by step, paid as you go, while you work).',
          'Some airlines run cadet schemes that help with the cost; the RAF trains pilots too, with a service commitment.',
        ],
        [
          q(
            'Which medical is needed for commercial training?',
            'A Class 1 medical',
            'A GP letter',
            'A dental check',
            'None',
          ),
          q('Integrated training is…', 'One full-time course', 'Step by step while working', 'Free', 'Only in the RAF'),
          q(
            'Can you wear glasses as a pilot?',
            'Yes, within limits',
            'Never',
            'Only on the ground',
            'Only as a captain',
          ),
          q(
            'Modular training lets you…',
            'Pay step by step while you work',
            'Skip the exams',
            'Fly without a licence',
            'Train in a week',
          ),
          q(
            'Who gives the Class 1 medical?',
            'A CAA-approved doctor',
            'Any pharmacist',
            'The airline’s HR team',
            'A flight instructor',
          ),
        ],
      ),
      lesson(
        'Your licences: private, commercial, airline',
        [
          'The Private Pilot Licence (PPL) needs at least 45 hours of flying and exams; it lets you fly for fun, not for pay.',
          'The Commercial Pilot Licence (CPL) adds more hours, the 13 ATPL theory exams, and an instrument rating to fly in cloud. Together they make a "frozen ATPL".',
          'A full Airline Transport Pilot Licence (ATPL) "unfreezes" at 1,500 flying hours, the licence a captain needs.',
        ],
        [
          q('Minimum flying hours for a PPL?', '45', '5', '500', '1,500'),
          q('How many ATPL theory exams are there?', '13', '3', '30', '1'),
          q('An ATPL unfreezes at…', '1,500 hours', '45 hours', '100 hours', '10,000 hours'),
          q(
            'An instrument rating lets you…',
            'Fly in cloud by instruments',
            'Play in the cockpit',
            'Fix the aircraft',
            'Fly for free',
          ),
          q('A PPL lets you…', 'Fly for fun, not for pay', 'Fly airliners', 'Fly for pay', 'Fly without insurance'),
        ],
      ),
      lesson(
        'Hours, type rating and the first job',
        [
          'A type rating trains you on one aircraft type (an Airbus A320, a Boeing 737) in the simulator and the air; airlines sometimes pay for it, sometimes you do.',
          'The first airline job is usually as a first officer on short-haul; some build hours instructing or flying smaller aircraft first.',
          'Every six months, pilots return to the simulator to pass checks; fail and you cannot fly.',
        ],
        [
          q('A type rating is for…', 'One aircraft type', 'Every aircraft', 'Helicopters only', 'Gliders'),
          q(
            'How often do airline pilots pass simulator checks?',
            'Every six months',
            'Once in their career',
            'Every ten years',
            'Daily',
          ),
          q(
            'A first airline job is usually…',
            'First officer on short-haul',
            'Captain on long-haul',
            'Chief pilot',
            'Test pilot',
          ),
          q('Some pilots build hours by…', 'Instructing', 'Watching films', 'Working in the terminal', 'Studying law'),
          q(
            'Who pays for a type rating?',
            'Sometimes the airline, sometimes the pilot',
            'Always the CAA',
            'Always the passengers',
            'Nobody',
          ),
        ],
      ),
      lesson(
        'From first officer to captain',
        [
          'Promotion to captain depends on hours, performance and seniority at the airline: often five to ten years, faster at fast-growing airlines.',
          'Captains earn well, but the job is also command: decisions in bad weather, sick passengers and diversions rest with them.',
          'Beyond captain: training captain, fleet manager, chief pilot; or long-haul, which pays most and takes you furthest.',
        ],
        [
          q('How long to captain, typically?', 'Five to ten years', 'One year', 'Twenty-five years', 'Six weeks'),
          q('A diversion means…', 'Landing somewhere other than planned', 'A holiday', 'A promotion', 'A pay rise'),
          q('Which pays most?', 'Long-haul', 'Short-haul', 'Instructing', 'Gliding'),
          q('A training captain…', 'Trains other pilots', 'Only flies freight', 'Sells tickets', 'Runs the airport'),
          q(
            'Promotion to captain depends on…',
            'Hours, performance and seniority',
            'Height',
            'Luck only',
            'Passengers voting',
          ),
        ],
      ),
    ],
  }),

  career({
    id: 'become_estate_agent',
    label: 'How to become a Property Pro',
    emoji: '🏘️',
    blurb:
      'Freehold and leasehold, the Land Registry, due diligence, agents, surveyors and conveyancers, and how property makes money.',
    skill: 'charisma',
    career: null,
    feePence: 8000,
    lessons: [
      lesson(
        'What property people do',
        [
          'Estate agents sell and let homes for a fee (a percentage of the sale, or a share of the rent). Surveyors value and inspect. Conveyancers do the legal transfer. Developers build.',
          'In England no licence is needed to be an estate agent, but you must belong to a redress scheme and register with HMRC for anti-money-laundering checks.',
          'Propertymark is the professional body with qualifications for agents; RICS is the chartered body for surveyors.',
        ],
        [
          q(
            'Is a licence needed to be an estate agent in England?',
            'No, but a redress scheme is',
            'Yes, from the DVLA',
            'Yes, from the council',
            'Yes, from the Land Registry',
          ),
          q(
            'Who does the legal transfer of a property?',
            'A conveyancer or solicitor',
            'The estate agent',
            'The bank',
            'The surveyor',
          ),
          q('RICS is the chartered body for…', 'Surveyors', 'Estate agents', 'Builders', 'Landlords'),
          q(
            'Estate agents must register with HMRC for…',
            'Anti-money-laundering checks',
            'VAT only',
            'Parking',
            'Insurance',
          ),
          q(
            'An estate agent’s fee for a sale is usually…',
            'A percentage of the price',
            'A fixed £10',
            'Paid by the buyer only',
            'Nothing',
          ),
        ],
      ),
      lesson(
        'Freehold, leasehold and the Land Registry',
        [
          'Freehold means you own the building and the land for ever. Leasehold means you own it for a fixed term (often 99 or 125 years) and pay ground rent and service charges; most flats are leasehold.',
          'HM Land Registry records who owns land in England and Wales; the title register shows the owner, mortgages and any rights over the land.',
          'An Energy Performance Certificate (EPC) is required before a property can be marketed.',
        ],
        [
          q('Most flats in England are…', 'Leasehold', 'Freehold', 'Commonhold', 'Unowned'),
          q(
            'Who records who owns land in England and Wales?',
            'HM Land Registry',
            'The council',
            'The estate agent',
            'Companies House',
          ),
          q('A lease of 99 years is…', 'Leasehold', 'Freehold', 'A tenancy', 'A mortgage'),
          q(
            'What is needed before marketing a property?',
            'An Energy Performance Certificate',
            'A planning permit',
            'A passport',
            'A survey by the buyer',
          ),
          q(
            'Leaseholders usually pay…',
            'Ground rent and service charges',
            'Nothing',
            'Council tax only',
            'The freeholder’s mortgage',
          ),
        ],
      ),
      lesson(
        'Due diligence: don’t buy trouble',
        [
          'Searches check planning, flooding, drainage and local plans. A survey (RICS Level 2 or 3) finds damp, subsidence and roof problems; the lender’s valuation is not a survey.',
          'Check the lease length (under 80 years is expensive to extend), the service charge accounts, and any cladding issues on blocks of flats.',
          'Stamp Duty Land Tax is paid by the buyer on purchases above the thresholds, which change with Budgets; first-time buyers get relief.',
        ],
        [
          q('A lender’s valuation is…', 'Not a survey', 'The best survey', 'Free legal advice', 'A planning search'),
          q('A lease under how many years gets expensive to extend?', '80', '200', '500', '20'),
          q('Who pays Stamp Duty Land Tax?', 'The buyer', 'The seller', 'The agent', 'The council'),
          q(
            'What does a Level 3 survey look for?',
            'Structural problems like subsidence',
            'Furniture',
            'The neighbours’ names',
            'Broadband speed',
          ),
          q('Searches check…', 'Planning, flooding and drainage', 'The seller’s job', 'Bus timetables', 'The weather'),
        ],
      ),
      lesson(
        'Agents, surveyors, conveyancers and lenders',
        [
          'A sale in England is not binding until exchange of contracts; before that either side can walk away ("gazumping" when a seller takes a higher offer).',
          'Completion is the day money moves and keys change hands. Mortgage lenders need a valuation; the Financial Conduct Authority regulates mortgage advice.',
          'Letting agents must protect tenants’ deposits in a government-approved scheme within 30 days.',
        ],
        [
          q(
            'When does a sale become binding in England?',
            'At exchange of contracts',
            'When an offer is accepted',
            'At the viewing',
            'When the EPC is done',
          ),
          q(
            'Gazumping is…',
            'A seller accepting a higher offer after agreeing one',
            'A kind of survey',
            'A tax',
            'A mortgage',
          ),
          q(
            'Completion is…',
            'When money moves and keys change hands',
            'The first viewing',
            'The survey',
            'The valuation',
          ),
          q(
            'Tenants’ deposits must be…',
            'Protected in an approved scheme within 30 days',
            'Kept in the agent’s wallet',
            'Spent on repairs',
            'Paid to the council',
          ),
          q(
            'Who regulates mortgage advice?',
            'The Financial Conduct Authority',
            'The Land Registry',
            'Propertymark',
            'The council',
          ),
        ],
      ),
      lesson(
        'How property makes money',
        [
          'Rental yield = annual rent ÷ price. Capital growth is the rise in value over years. Buy-to-let landlords pay tax on rent and extra stamp duty on second homes.',
          'Adding value: extensions, lofts, splitting a house into flats (with planning and building regulations), or buying at auction below market.',
          'Costs eat returns: mortgage interest, service charges, repairs, voids (empty months), letting fees, and the licensing many councils require of landlords.',
        ],
        [
          q(
            'Rental yield is…',
            'Annual rent divided by price',
            'Price divided by rent',
            'Rent minus tax',
            'The deposit',
          ),
          q('A "void" is…', 'A month with no tenant', 'A broken lift', 'A tax', 'A type of lease'),
          q('Second homes pay…', 'Extra stamp duty', 'No stamp duty', 'Half council tax', 'No tax on rent'),
          q(
            'Splitting a house into flats needs…',
            'Planning and building regulations approval',
            'Nothing',
            'The neighbours’ permission only',
            'A new EPC only',
          ),
          q(
            'Capital growth is…',
            'The rise in a property’s value over time',
            'The rent',
            'The agent’s fee',
            'The mortgage rate',
          ),
        ],
      ),
    ],
  }),

  career({
    id: 'become_journalist',
    label: 'How to become a Journalist',
    emoji: '🎙️',
    blurb:
      'The newsroom, the NCTJ, getting the story right, the law and ethics, and finding your voice on air and online.',
    skill: 'comedy',
    career: null,
    feePence: 7000,
    lessons: [
      lesson(
        'What journalists and broadcasters do',
        [
          'Reporters find stories, check them and write or broadcast them; editors decide what runs; producers make radio and TV happen; presenters front it.',
          'Local papers and radio train most journalists; national papers, the BBC, ITV, Sky and online outlets are where many aim.',
          'Deadlines are real: a story that is right but late is no story.',
        ],
        [
          q('Who decides what runs?', 'Editors', 'Reporters', 'Readers', 'Printers'),
          q(
            'Where do most journalists train first?',
            'Local papers and radio',
            'Parliament',
            'Hollywood',
            'Advertising',
          ),
          q(
            'A producer…',
            'Makes radio and TV programmes happen',
            'Only reads the news',
            'Sells advertising',
            'Fixes cameras',
          ),
          q('A story that is right but late is…', 'No story', 'Still the front page', 'Worth more', 'An editorial'),
          q('Who fronts a programme on air?', 'The presenter', 'The producer', 'The editor', 'The sub-editor'),
        ],
      ),
      lesson(
        'Training and your first newsroom',
        [
          'The NCTJ Diploma in Journalism is the industry qualification, taught at colleges and universities, with shorthand (traditionally 100 words a minute), media law and public affairs.',
          'Apprenticeships at the BBC, ITV and local news groups pay while you train. A degree in anything helps; a journalism degree is not required.',
          'Build a portfolio: a blog, student media, a podcast, freelance pieces for local outlets.',
        ],
        [
          q(
            'What is the NCTJ Diploma?',
            'The industry journalism qualification',
            'A broadcasting licence',
            'A press pass',
            'A degree in law',
          ),
          q(
            'Traditional shorthand speed for the NCTJ?',
            '100 words a minute',
            '20 words a minute',
            '500 words a minute',
            'No shorthand',
          ),
          q('Is a journalism degree required?', 'No', 'Yes', 'Only for TV', 'Only for radio'),
          q(
            'Which run journalism apprenticeships?',
            'The BBC, ITV and local news groups',
            'Only universities',
            'Only newspapers abroad',
            'Nobody',
          ),
          q(
            'A good first portfolio includes…',
            'Student media, a blog or podcast',
            'Only a CV',
            'A photo',
            'Your exam results',
          ),
        ],
      ),
      lesson(
        'Getting the story right',
        [
          'Verify before you publish: two independent sources for a disputed fact, documents where possible, and a right of reply to anyone criticised.',
          'The inverted pyramid: the most important facts first (who, what, where, when, why), then detail. Attribute claims ("the council said") so readers know where they come from.',
          'Corrections are published promptly and openly; trust is the only thing a journalist sells.',
        ],
        [
          q('How many independent sources for a disputed fact?', 'Two', 'None', 'Ten', 'One is plenty'),
          q(
            'The inverted pyramid puts…',
            'The most important facts first',
            'The ending first',
            'Opinion first',
            'The byline last',
          ),
          q(
            'A right of reply is…',
            'Giving someone criticised a chance to respond',
            'A reader’s letter',
            'A libel claim',
            'A press release',
          ),
          q(
            'When a story is wrong, a journalist…',
            'Publishes a correction promptly',
            'Deletes it quietly',
            'Blames the source',
            'Waits a year',
          ),
          q('"The council said" is an example of…', 'Attribution', 'Libel', 'Shorthand', 'A headline'),
        ],
      ),
      lesson(
        'Ethics and the law',
        [
          'Newspapers follow the Editors’ Code, overseen by IPSO (most) or Impress; broadcasters follow the Ofcom Broadcasting Code, which requires due impartiality.',
          'Defamation (libel): a false statement that seriously harms someone’s reputation can cost a fortune. Truth, honest opinion and public interest are defences.',
          'Contempt of court: once a case is "active" (an arrest or charge), reporting must not prejudice the trial. Victims of sexual offences have lifelong anonymity.',
        ],
        [
          q(
            'Which code do UK broadcasters follow?',
            'The Ofcom Broadcasting Code',
            'The Editors’ Code',
            'The Highway Code',
            'The Green Book',
          ),
          q('Who oversees most newspapers’ standards?', 'IPSO', 'Ofcom', 'The BBC', 'The police'),
          q(
            'Libel is…',
            'A false statement that seriously harms reputation',
            'Any criticism',
            'A true story',
            'An opinion piece',
          ),
          q(
            'A case becomes "active" for contempt when…',
            'Someone is arrested or charged',
            'The story is published',
            'The verdict is in',
            'The judge retires',
          ),
          q('Broadcasters must show…', 'Due impartiality', 'Party loyalty', 'No news', 'Only sport'),
        ],
      ),
      lesson(
        'On air and online',
        [
          'Radio and TV need a clear voice, short sentences and the ability to talk to time. Read everything aloud; write for the ear.',
          'Online, headlines and the first line decide whether anyone reads on; video and audio clips travel further than text.',
          'Specialise (politics, crime, health, sport, tech) and your contacts book becomes your career.',
        ],
        [
          q(
            'Writing for broadcast means…',
            'Short sentences for the ear',
            'Long paragraphs',
            'Latin terms',
            'Footnotes',
          ),
          q(
            '"Talking to time" means…',
            'Finishing exactly when the slot ends',
            'Speaking slowly',
            'Reading the clock',
            'Talking about history',
          ),
          q(
            'Online, what decides whether people read on?',
            'The headline and first line',
            'The byline',
            'The date',
            'The font',
          ),
          q('A contacts book is…', 'Your sources and who to call', 'A diary', 'A camera', 'A press card'),
          q(
            'Specialising in a beat…',
            'Builds expertise and contacts',
            'Limits you for ever',
            'Is against the code',
            'Pays less always',
          ),
        ],
      ),
    ],
  }),

  career({
    id: 'become_entrepreneur',
    label: 'How to become an Entrepreneur',
    emoji: '🚀',
    blurb:
      'Find a problem worth solving, test it small, make it official with HMRC and Companies House, keep the books, and grow.',
    skill: 'hustle',
    career: 'run_stall',
    feePence: 6000,
    lessons: [
      lesson(
        'Find a problem worth solving',
        [
          'Businesses that last solve a problem people already pay to solve, badly. Watch for queues, complaints and workarounds.',
          'Talk to twenty potential customers before building anything. Ask what they do now and what it costs them; don’t ask whether they like your idea.',
          'Your first customers are usually people like you: you understand the problem because you have it.',
        ],
        [
          q(
            'Good businesses solve problems that people…',
            'Already pay to solve, badly',
            'Have never noticed',
            'Enjoy',
            'Cannot describe',
          ),
          q(
            'Before building anything, talk to…',
            'Around twenty potential customers',
            'An investor',
            'Nobody',
            'A lawyer',
          ),
          q(
            'What should you avoid asking people?',
            'Whether they like your idea',
            'What they do now',
            'What it costs them',
            'How often it happens',
          ),
          q(
            'A sign of a real problem is…',
            'Queues, complaints and workarounds',
            'A big logo',
            'A celebrity',
            'A patent',
          ),
          q('First customers are often…', 'People like you', 'Governments', 'Banks', 'Strangers abroad'),
        ],
      ),
      lesson(
        'Start small and test it',
        [
          'A minimum viable product is the smallest thing you can sell to learn: a market stall before a shop, a landing page before an app, ten hand-made units before a factory.',
          'Measure: how many bought, how many came back, what they said. Change one thing at a time.',
          'Keep your job until the business pays you; most founders start on evenings and weekends.',
        ],
        [
          q(
            'A minimum viable product is…',
            'The smallest thing you can sell to learn',
            'A perfect product',
            'A business plan',
            'A loan',
          ),
          q('What do you test before opening a shop?', 'A market stall', 'A chain', 'A franchise', 'An IPO'),
          q(
            'When should you quit your job?',
            'When the business pays you',
            'Day one',
            'Before you have an idea',
            'Never',
          ),
          q(
            'What matters most to measure early?',
            'Who bought and who came back',
            'Likes',
            'Office size',
            'Logo colours',
          ),
          q('When testing, change…', 'One thing at a time', 'Everything at once', 'Nothing', 'Only the price'),
        ],
      ),
      lesson(
        'Make it official',
        [
          'A sole trader registers with HMRC for Self Assessment and keeps the profits personally; a limited company is registered at Companies House, is a separate legal person, and files accounts every year.',
          'Open a business bank account, get public liability insurance if you deal with the public, and check whether you need a licence (food, street trading, alcohol).',
          'VAT registration becomes compulsory once your turnover passes the threshold; many register earlier to reclaim VAT on costs.',
        ],
        [
          q('Where is a limited company registered?', 'Companies House', 'HMRC only', 'The council', 'The bank'),
          q(
            'A sole trader registers with…',
            'HMRC for Self Assessment',
            'Companies House',
            'The police',
            'The Post Office',
          ),
          q('A limited company is…', 'A separate legal person', 'The same as its owner', 'Tax free', 'Always big'),
          q(
            'Selling food on the street needs…',
            'Licences from the council',
            'Nothing',
            'A passport',
            'A limited company',
          ),
          q(
            'VAT registration becomes compulsory when…',
            'Turnover passes the threshold',
            'You hire staff',
            'You open a bank account',
            'You make a loss',
          ),
        ],
      ),
      lesson(
        'Money matters: price, records, separate accounts',
        [
          'Price on value, not cost; then check the margin covers everything, including your own time. Cash flow kills more businesses than losses: chase invoices and keep a cushion.',
          'Keep business and personal money separate. Record every sale and expense (HMRC’s Making Tax Digital means software, not shoeboxes) and keep records for years.',
          'Start Up Loans (government-backed) and local grants help early; investors want a share of the company, so think before you give it away.',
        ],
        [
          q(
            'What kills more businesses than losses?',
            'Running out of cash',
            'High prices',
            'Good reviews',
            'Too many customers',
          ),
          q(
            'Business and personal money should be…',
            'Kept separate',
            'Mixed for simplicity',
            'In cash only',
            'Abroad',
          ),
          q(
            'Making Tax Digital means…',
            'Keeping records in software',
            'Paying tax in bitcoin',
            'No tax',
            'Paper only',
          ),
          q('Investors usually want…', 'A share of the company', 'Interest only', 'Nothing', 'Your car'),
          q('Price should be based on…', 'The value to the customer', 'Cost alone', 'What feels nice', 'Your rent'),
        ],
      ),
      lesson(
        'Grow it',
        [
          'Growth means systems: written processes, your first hire (an employer must register with HMRC for PAYE, pay at least the minimum wage and enrol staff in a pension), and saying no to work that does not fit.',
          'Repeat customers cost far less than new ones; a referral is the best advert. Reviews and a simple website do the rest.',
          'Know your numbers every week: sales, margin, cash in the bank, and what you owe.',
        ],
        [
          q('An employer must register with HMRC for…', 'PAYE', 'VAT only', 'Nothing', 'Companies House'),
          q('Staff must be enrolled in…', 'A workplace pension', 'A gym', 'A union', 'A share scheme'),
          q(
            'Which customers cost least?',
            'Repeat customers',
            'New customers',
            'Overseas customers',
            'Government customers',
          ),
          q(
            'Which numbers should a founder know weekly?',
            'Sales, margin, cash and what is owed',
            'Followers only',
            'Staff birthdays',
            'Competitors’ rent',
          ),
          q('The best advert is…', 'A referral', 'A billboard', 'A discount', 'A mascot'),
        ],
      ),
    ],
  }),

  career({
    id: 'become_civil_engineer',
    label: 'How to become a Civil Engineer',
    emoji: '🏗️',
    blurb:
      'Roads, bridges and tunnels: the accredited degree, apprenticeships, chartership with the ICE, and life on site.',
    skill: 'coding',
    career: null,
    feePence: 10000,
    lessons: [
      lesson(
        'What civil engineers build',
        [
          'Civil engineers design and build the things everyone uses: roads, railways, bridges, tunnels, water and sewage, flood defences, airports and the ground beneath buildings.',
          'London’s Crossrail (the Elizabeth line), the Thames Tideway super sewer and HS2 are civil engineering on the grandest scale; a new roundabout is civil engineering too.',
          'Structural engineers make sure it stands up; geotechnical engineers deal with the ground; transport engineers with how it all flows.',
        ],
        [
          q('Which of these is civil engineering?', 'A flood defence', 'A smartphone', 'A vaccine', 'A novel'),
          q('The Thames Tideway is…', 'A super sewer under the Thames', 'A bridge', 'A railway', 'An airport'),
          q('Geotechnical engineers deal with…', 'The ground', 'Electrics', 'Traffic lights', 'Paint'),
          q('The Elizabeth line was built by the project called…', 'Crossrail', 'Thameslink', 'HS2', 'Tideway'),
          q('A structural engineer makes sure…', 'It stands up', 'It is cheap', 'It is painted', 'It is on time'),
        ],
      ),
      lesson(
        'The way in: maths, physics and a degree',
        [
          'A-levels in maths and physics (or an engineering T-level or BTEC) lead to a BEng (three years) or MEng (four years) accredited by the Institution of Civil Engineers (ICE).',
          'Degree apprenticeships (Level 6) pay you to work for a contractor or consultant while studying one day a week: no fees, five to six years.',
          'The ICE QUEST scholarship and company sponsorships help with costs.',
        ],
        [
          q(
            'Which A-levels lead to civil engineering?',
            'Maths and physics',
            'Art and drama',
            'French and history',
            'PE and music',
          ),
          q(
            'Who accredits civil engineering degrees?',
            'The Institution of Civil Engineers',
            'The council',
            'HMRC',
            'The Highways Agency',
          ),
          q(
            'A degree apprenticeship in engineering…',
            'Pays you to work while you study',
            'Is unpaid',
            'Takes one year',
            'Needs no maths',
          ),
          q('MEng takes…', 'Four years', 'One year', 'Ten years', 'Six months'),
          q('QUEST is…', 'An ICE scholarship', 'A bridge', 'A software package', 'A tax'),
        ],
      ),
      lesson(
        'Placements, graduate jobs and chartership',
        [
          'Summer placements and a year in industry get you onto a graduate scheme with a contractor (building it) or a consultant (designing it).',
          'Professional registration: Incorporated Engineer (IEng) or Chartered Engineer (CEng) through the ICE, after years of recorded experience and a professional review interview.',
          'Chartered engineers sign off designs, earn more and can work anywhere in the world.',
        ],
        [
          q('CEng stands for…', 'Chartered Engineer', 'Civil Engineer', 'Certified Engineer', 'Chief Engineer'),
          q(
            'Who awards chartership to civil engineers?',
            'The Institution of Civil Engineers',
            'The university',
            'The employer',
            'The DVLA',
          ),
          q('A contractor…', 'Builds it', 'Designs it only', 'Insures it', 'Owns it'),
          q(
            'What ends the chartership process?',
            'A professional review interview',
            'A driving test',
            'A written exam only',
            'A lottery',
          ),
          q('A year in industry is…', 'A paid placement during a degree', 'A gap year', 'A resit', 'A holiday'),
        ],
      ),
      lesson(
        'Life on site',
        [
          'Site work starts early: the site diary, the day’s programme, checking what the subcontractors are doing against the drawings, and solving what the ground throws up.',
          'Safety is the law: the Construction (Design and Management) Regulations 2015, a CSCS card to get on site, hard hat, boots and hi-vis always.',
          'Consultants work from offices on drawings, calculations and models (BIM: building information modelling), visiting site to check.',
        ],
        [
          q(
            'Which regulations govern construction safety?',
            'CDM 2015',
            'The Highway Code',
            'GDPR',
            'The Food Safety Act',
          ),
          q('What card gets you onto a site?', 'A CSCS card', 'A library card', 'A bus pass', 'A credit card'),
          q(
            'BIM stands for…',
            'Building information modelling',
            'British industrial metal',
            'Bridge inspection manual',
            'Basic infrastructure maths',
          ),
          q(
            'A site diary records…',
            'What happened on site each day',
            'Lunch orders',
            'The weather forecast only',
            'Staff birthdays',
          ),
          q('On site you always wear…', 'Hard hat, boots and hi-vis', 'A suit', 'Trainers', 'Headphones'),
        ],
      ),
      lesson(
        'Building right in London',
        [
          'London is built on clay that shrinks and swells, with rivers, Tube tunnels and Roman remains underground; ground investigation comes before design.',
          'Planning permission from the borough, building regulations approval, and the Environment Agency for anything near the Thames.',
          'The future is low carbon: reusing structures, less concrete and steel, and designing for floods that are coming.',
        ],
        [
          q('What is under much of London?', 'Clay that shrinks and swells', 'Solid granite', 'Sand dunes', 'Ice'),
          q(
            'Who gives planning permission in London?',
            'The borough council',
            'The Mayor alone',
            'The ICE',
            'The Tube',
          ),
          q('Work near the Thames may need…', 'The Environment Agency', 'The BBC', 'The Navy', 'A passport'),
          q(
            'What comes before design?',
            'Ground investigation',
            'The opening ceremony',
            'The paint',
            'The press release',
          ),
          q(
            'Low-carbon engineering means…',
            'Less concrete and steel, more reuse',
            'Bigger buildings',
            'More cars',
            'No bridges',
          ),
        ],
      ),
    ],
  }),

  career({
    id: 'money_smart',
    label: 'Money Smart',
    emoji: '💰',
    blurb: 'A life skill: budgeting, saving, spotting scams, borrowing wisely and making money grow, UK style.',
    skill: 'hustle',
    career: null,
    feePence: 2500,
    lessons: [
      lesson(
        'Budgeting: know where it goes',
        [
          'Write down what comes in and what goes out for a month: rent, bills, food, travel, fun. Most people are surprised.',
          'A simple rule: 50% needs, 30% wants, 20% saving and paying off debt. Adjust it to your life, but keep the saving.',
          'Check your bank app weekly; set direct debits for bills the day after payday so they are never missed.',
        ],
        [
          q('In the 50/30/20 rule, 20% goes to…', 'Saving and paying off debt', 'Fun', 'Rent', 'Food'),
          q('How often should you check your spending?', 'Weekly', 'Yearly', 'Never', 'Only when overdrawn'),
          q(
            'When should bill direct debits go out?',
            'Just after payday',
            'The day before payday',
            'Randomly',
            'Only at Christmas',
          ),
          q(
            'The first step to a budget is…',
            'Writing down what comes in and goes out',
            'Cutting all fun',
            'Buying a safe',
            'Getting a loan',
          ),
          q('"Needs" include…', 'Rent and bills', 'Concert tickets', 'Takeaways', 'New trainers'),
        ],
      ),
      lesson(
        'Saving: the emergency fund and ISAs',
        [
          'First build an emergency fund of three to six months’ costs in an easy-access account, so a broken boiler is not a crisis.',
          'An ISA lets UK adults save or invest up to £20,000 a year with no tax on the interest or growth; Lifetime ISAs add a 25% government bonus towards a first home or retirement.',
          'Pay yourself first: move the saving on payday, not what is left at the end.',
        ],
        [
          q(
            'How big should an emergency fund be?',
            'Three to six months’ costs',
            'One week’s costs',
            'Ten years’ costs',
            '£10',
          ),
          q('The yearly ISA allowance is…', '£20,000', '£2,000', '£200,000', 'Unlimited'),
          q('What is not taxed in an ISA?', 'Interest and growth', 'Your salary', 'Your rent', 'Council tax'),
          q('A Lifetime ISA adds…', 'A 25% government bonus', 'Nothing', 'A 1% fee', 'A loan'),
          q(
            '"Pay yourself first" means…',
            'Save on payday before spending',
            'Buy a treat first',
            'Pay your boss',
            'Skip the bills',
          ),
        ],
      ),
      lesson(
        'Spot the scam',
        [
          'Banks, HMRC and the police never ask you to move money to a "safe account", read out a one-time code, or pay in gift cards. Hang up and call the number on your card.',
          'Check any investment firm on the FCA register; guaranteed high returns and pressure to act today are the signs of a scam.',
          'Take Five: stop, challenge, protect. Report fraud to Action Fraud; the bank must refund most authorised push payment scams.',
        ],
        [
          q(
            'A caller asks you to move money to a "safe account". You…',
            'Hang up and call the number on your card',
            'Do it quickly',
            'Give them a code',
            'Send gift cards',
          ),
          q(
            'Where do you check an investment firm?',
            'The FCA register',
            'A forum',
            'Their own website only',
            'Companies House alone',
          ),
          q('A guaranteed high return is…', 'A sign of a scam', 'Normal', 'A bank product', 'Tax free'),
          q('Who do you report fraud to?', 'Action Fraud', 'The Post Office', 'HMRC', 'Ofcom'),
          q('Take Five means…', 'Stop, challenge, protect', 'Pay five pounds', 'Five passwords', 'Wait five years'),
        ],
      ),
      lesson(
        'Borrowing wisely',
        [
          'APR is the yearly cost of borrowing including fees; lower is better. A credit card paid in full each month costs nothing; paying the minimum can take decades.',
          'Your credit report (free from Experian, Equifax and TransUnion) decides what you can borrow; register to vote and pay on time to build it.',
          'Buy-now-pay-later is still debt. Payday loans and loan sharks are the most expensive money in Britain; StepChange and Citizens Advice help for free if it goes wrong.',
        ],
        [
          q('APR is…', 'The yearly cost of borrowing including fees', 'A savings rate', 'A bank charge', 'A tax'),
          q(
            'A credit card paid in full each month costs…',
            'Nothing in interest',
            'The APR',
            'A monthly fee always',
            'Half the balance',
          ),
          q(
            'How much does a credit report cost?',
            'Nothing from the main agencies',
            '£50',
            '£5 a month',
            'A share of your salary',
          ),
          q(
            'What helps build a credit score?',
            'Registering to vote and paying on time',
            'Never borrowing',
            'Moving house often',
            'Using cash only',
          ),
          q(
            'Who gives free debt advice?',
            'StepChange and Citizens Advice',
            'Loan sharks',
            'Payday lenders',
            'Casinos',
          ),
        ],
      ),
      lesson(
        'Making money grow',
        [
          'Compound interest: growth on growth. £100 a month at 7% a year is about £120,000 after 30 years; starting ten years later roughly halves it.',
          'Workplace pensions: you are auto-enrolled, your employer must pay in too, and the government adds tax relief; opting out throws money away.',
          'Cheap global index funds beat most professionals over time. Investing is for money you will not need for five years or more.',
        ],
        [
          q('Compound interest is…', 'Growth on growth', 'A flat fee', 'A tax', 'A loan'),
          q(
            'In a workplace pension, who else pays in?',
            'Your employer and the government',
            'Nobody',
            'Your landlord',
            'Your bank',
          ),
          q('Investing is for money you will not need for…', 'Five years or more', 'A week', 'A month', 'Tomorrow'),
          q(
            'Starting to invest ten years later roughly…',
            'Halves the final pot',
            'Doubles it',
            'Makes no difference',
            'Triples it',
          ),
          q(
            'Cheap global index funds…',
            'Beat most professionals over time',
            'Are illegal',
            'Are guaranteed',
            'Only lose money',
          ),
        ],
      ),
    ],
  }),
];
